/**
 * venues.js — the exchanges the ingestor feeds, in one place.
 *
 * Radar, the Screener's coin list and the Impulse feed all need the same list
 * with the same colours and labels; three copies would drift the first time a
 * venue is added. `id` matches the Go scanner's venue name and the suffix in the
 * `global_update_<id>` frames it publishes.
 */
export const VENUES = [
  { id: 'futures',     exchange: 'binance', label: 'Binance FUT',  short: 'BIN·F', c: '#f0b90b' },
  { id: 'spot',        exchange: 'binance', label: 'Binance SPOT', short: 'BIN·S', c: '#f0b90b' },
  { id: 'bybit',       exchange: 'bybit',   label: 'Bybit FUT',    short: 'BYB·F', c: '#f7a600' },
  { id: 'bybit_spot',  exchange: 'bybit',   label: 'Bybit SPOT',   short: 'BYB·S', c: '#f7a600' },
  { id: 'okx',         exchange: 'okx',     label: 'OKX FUT',      short: 'OKX·F', c: '#8b7bf0' },
  { id: 'okx_spot',    exchange: 'okx',     label: 'OKX SPOT',     short: 'OKX·S', c: '#8b7bf0' },
  { id: 'bitget',      exchange: 'bitget',  label: 'Bitget FUT',   short: 'BG·F',  c: '#00d1c1' },
  { id: 'bitget_spot', exchange: 'bitget',  label: 'Bitget SPOT',  short: 'BG·S',  c: '#00d1c1' },
];

/** One chip per EXCHANGE (both markets folded together) — what the Screener and
 *  the Impulse feed offer, since a chart is per-coin, not per-market. */
export const EXCHANGES = [
  { id: 'binance', label: 'Binance', c: '#f0b90b' },
  { id: 'bybit',   label: 'Bybit',   c: '#f7a600' },
  { id: 'okx',     label: 'OKX',     c: '#8b7bf0' },
  { id: 'bitget',  label: 'Bitget',  c: '#00d1c1' },
];

export const ALL_EXCHANGE_IDS = EXCHANGES.map((e) => e.id);

export const venueById = (id) => VENUES.find((v) => v.id === id) || null;
export const exchangeOf = (venueId) => venueById(venueId)?.exchange || 'binance';

/* ─── Pair filtering ────────────────────────────────────────────────────────
 * Lives here rather than in a hook: it is pure data used by charts, tables and
 * the ticker feed alike, none of which should have to import React to use it.
 *
 * SEPARATE LIST from backend-node/src/lib/stablecoins.js on purpose — this is
 * frontend code (a Vite/browser bundle) and cannot import a Node backend
 * module without a shared-package refactor nobody has asked for. But they
 * answer the SAME question ("is this a $1-pegged asset, not a real coin") for
 * different consumers (this one: chart/venue universe; that one: which
 * detectors are allowed to publish a situation on it) — found live 20.08.2026
 * that they had already diverged (this list was missing EURC/USDX/CUSD/
 * CRVUSD/GHO/USDY, that one was missing EURI/AEUR/XUSD/RLUSD/USDG/EURS),
 * synced both directions here. If you add a base to ONE of these two files,
 * add it to the other too — see that file's own matching comment.
 */
const STABLE_BASES = new Set([
  'USDC', 'USDT', 'BUSD', 'FDUSD', 'TUSD', 'USDP', 'USDD', 'DAI', 'USDE',
  'USD1', 'PYUSD', 'EURI', 'AEUR', 'USDS', 'LUSD', 'GUSD', 'FRAX', 'SUSD',
  'USTC', 'XUSD', 'RLUSD', 'USDG', 'EURT', 'EURS',
  // Added 20.08.2026 to match backend-node/src/lib/stablecoins.js:
  'EURC', 'USDX', 'CUSD', 'CRVUSD', 'GHO', 'USDY',
]);
const QUOTES = ['USDT', 'USDC', 'BUSD', 'FDUSD', 'TUSD', 'USD'];

// Permanently hidden tickers — not stablecoins, just coins the owner never wants
// surfaced. Separate set so the reason for each exclusion stays readable.
const EXCLUDED_BASES = new Set(['U', 'BFUSD']);

/** true when the pair is dollar↔dollar OR the base is permanently excluded. */
export function isStablePair(symbol) {
  const s = String(symbol || '').toUpperCase();
  for (const q of QUOTES) {
    if (s.endsWith(q) && s.length > q.length) {
      const base = s.slice(0, -q.length);
      return STABLE_BASES.has(base) || EXCLUDED_BASES.has(base);
    }
  }
  return false;
}
